import {
    DEFAULT_POLICY_ID,
    ENTRA_ID_SSO_APP_OFFSET,
    MFA_TYPES,
    MFA_WINDOW_TYPES,
    POLICY_MATCHING_BY_OPTIONS,
    POLICY_MODE_TYPES,
    POLICY_MATCHING_BY_OPTIONS_SOURCE_HOSTNAME_LABEL
} from '@silverfort-nodelibs/common/utils/consts';

export const getPoliciesListByAction = (policiesList, actionType, filteredPoliciesIds) => {
    const actionPolicies = [];
    for (const policy of policiesList) {
        if (policy.action_type === actionType && policy.id > DEFAULT_POLICY_ID && !isNaN(policy.id)) {
            if (Array.isArray(filteredPoliciesIds) && !filteredPoliciesIds.includes(policy.id)) {
                continue;
            }
            actionPolicies.push(policy);
        }
    }
    return actionPolicies;
};

export const sortPolicies = (policies, orderType, priorityOrder) => {
    let sortedPolicies = [];
    // Sort policies by state and name
    if (orderType === 'state') {
        sortedPolicies = policies.sort((first, second) => {
            if (first.state !== second.state) {
                return first.state ? -1 : 1;
            }

            if (first.name !== second.name) {
                return first.name < second.name ? -1 : 1;
            }

            return first.id < second.id ? -1 : 1;
        });
    }

    // Sort policies by priority
    else if (orderType === 'priority') {
        sortedPolicies = policies.sort((first, second) => {
            const firstPriorityIndex = priorityOrder.findIndex((priorityObj) => priorityObj.policyId === first.id);
            const secondPriorityObjIndex = priorityOrder.findIndex((priorityObj) => priorityObj.policyId === second.id);
            if (firstPriorityIndex === -1) {
                return 1;
            } else if (secondPriorityObjIndex === -1) {
                return -1;
            }
            return firstPriorityIndex < secondPriorityObjIndex ? -1 : 1;
        });
    } else {
        sortedPolicies = policies;
    }
    return sortedPolicies;
};

export const removePolicyById = (policyId, policiesList) => {
    const policyIndex = policiesList.findIndex((policy) => policy.id === policyId);
    if (policyIndex > -1) {
        policiesList.splice(policyIndex, 1);
        return policiesList;
    }
    return policiesList;
};

export const calcMfaWindow = (mfaWindow) => {
    let newMfaWindowType = MFA_WINDOW_TYPES.MINUTES.value;
    let newMfaWindowValueByType = mfaWindow;
    let currentMfaWindowValue = newMfaWindowValueByType / MFA_WINDOW_TYPES.DAYS.value;
    if (currentMfaWindowValue >= 1 && currentMfaWindowValue % 1 === 0) {
        newMfaWindowType = MFA_WINDOW_TYPES.DAYS.value;
        newMfaWindowValueByType = currentMfaWindowValue;
    } else {
        currentMfaWindowValue = newMfaWindowValueByType / MFA_WINDOW_TYPES.HOURS.value;
        if (currentMfaWindowValue >= 1 && currentMfaWindowValue % 1 === 0) {
            newMfaWindowType = MFA_WINDOW_TYPES.HOURS.value;
            newMfaWindowValueByType = currentMfaWindowValue;
        }
    }
    return [newMfaWindowType, newMfaWindowValueByType];
};

export const changeTokensAllowed = (value, currentTokensAllowed = [], allowOtpAndHardware = false) => {
    let newFieldValue = [];
    if (value?.length) {
        newFieldValue = value;
        const newValues = newFieldValue.filter((value) => !(currentTokensAllowed || []).includes(value));

        for (const value of newValues) {
            // If allowed_token_types[0] is a bigger than 10000, it indicates an Entra ID token.
            // Since Entra ID tokens are should not be combined with other tokens, and act like bridge policy,
            // we ensure that only one Entra ID token is allowed to be selected.
            // * Disclaimer * - This is a temporary solution until we refactor Policy module.
            const isEntraIdToken = (value) => value > ENTRA_ID_SSO_APP_OFFSET;
            if (isEntraIdToken(value)) {
                newFieldValue = [value];
                break;
            } else {
                newFieldValue = newFieldValue.filter((value) => !isEntraIdToken(value));
            }

            if (value === MFA_TYPES.CUSTOM_MFA.ID) {
                newFieldValue = [MFA_TYPES.CUSTOM_MFA.ID];
            } else if (value === MFA_TYPES.HARDWARE.ID) {
                if (!allowOtpAndHardware && newFieldValue.includes(MFA_TYPES.OTP.ID)) {
                    newFieldValue = newFieldValue.filter((value) => value !== MFA_TYPES.OTP.ID);
                }
            } else if (value === MFA_TYPES.OTP.ID) {
                if (!allowOtpAndHardware && newFieldValue.includes(MFA_TYPES.HARDWARE.ID)) {
                    newFieldValue = newFieldValue.filter((value) => value !== MFA_TYPES.HARDWARE.ID);
                }
            } else if (value === MFA_TYPES.PING_ADAPTER.ID) {
                newFieldValue = [MFA_TYPES.PING_ADAPTER.ID];
            } else if (value === MFA_TYPES.CHROME.ID) {
                if (newFieldValue.includes(MFA_TYPES.HARDWARE.ID)) {
                    newFieldValue = newFieldValue.filter((value) => value !== MFA_TYPES.HARDWARE.ID);
                }
            } else if (value === MFA_TYPES.DESKTOP_APP.ID || value === MFA_TYPES.MS_TEAMS.ID) {
                if (newFieldValue.includes(MFA_TYPES.HARDWARE.ID)) {
                    newFieldValue = newFieldValue.filter((value) => value !== MFA_TYPES.HARDWARE.ID);
                }
                if (newFieldValue.includes(MFA_TYPES.OTP.ID)) {
                    newFieldValue = newFieldValue.filter((value) => value !== MFA_TYPES.OTP.ID);
                }
            }
        }

        if (newFieldValue.includes(MFA_TYPES.PING_ADAPTER.ID) && newFieldValue.length > 1) {
            newFieldValue = newFieldValue.filter((value) => value !== MFA_TYPES.PING_ADAPTER.ID);
        }
    }
    return newFieldValue;
};

export const getAllowedTokensOptions = (
    currentSelectedTokens,
    configuredTokens,
    desktopEnabled,
    pingFedEnabled,
    modeType,
    radiusEnabled,
    isSaas,
    externalMfaTokens
) => {
    const predefinedTokensOptions = Object.keys(MFA_TYPES)
        .filter((tokenType) => MFA_TYPES[tokenType].ID !== MFA_TYPES.AZURE_SSO.ID)
        .filter((tokenType) => MFA_TYPES[tokenType].ID !== MFA_TYPES.BRIDGE_SSO.ID)
        .filter((tokenType) => MFA_TYPES[tokenType].ID !== MFA_TYPES.ENTRA_ID.ID)
        .filter((tokenType) => MFA_TYPES[tokenType].ID !== MFA_TYPES.MS_TEAMS.ID) // TODO AF-52136: remove when MS Teams MFA push is wanted to be shown
        .filter((tokenType) => shouldShowInCurrentMode(MFA_TYPES[tokenType], isSaas))
        .map((tokenType) => {
            const isConfigured = isTokenConfigured(
                tokenType,
                currentSelectedTokens,
                configuredTokens,
                desktopEnabled,
                pingFedEnabled,
                modeType,
                radiusEnabled
            );

            const displayName = MFA_TYPES[tokenType].DISPLAY_NAME;
            const disabledTokenText = MFA_TYPES[tokenType].DISABLED_DISPLAY_NAME;
            const isCustomMfaConfigured =
                currentSelectedTokens && currentSelectedTokens.includes(MFA_TYPES.CUSTOM_MFA.ID);

            return {
                value: MFA_TYPES[tokenType].ID,
                label: isConfigured
                    ? displayName
                    : disabledTokenText && !isCustomMfaConfigured
                    ? disabledTokenText
                    : displayName,
                disabled: !isConfigured,
                tokenType: tokenType
            };
        });

    const activePredefinedTokens = predefinedTokensOptions.filter((token) => !token.disabled);
    const disabledPredefinedTokens = predefinedTokensOptions.filter((token) => token.disabled);

    let externalTokensOptions = [];
    if (externalMfaTokens?.length > 0) {
        externalTokensOptions = externalMfaTokens.map(({ id, displayName }) => ({
            value: id,
            label: displayName,
            disabled: false,
            tokenType: 'external'
        }));
    }

    return [...activePredefinedTokens, ...externalTokensOptions, ...disabledPredefinedTokens];
};

const isTokenConfigured = (
    tokenName,
    currentSelectedTokens,
    configuredTokens,
    desktopEnabled,
    pingFedEnabled,
    modeType,
    radiusEnabled
) => {
    if (currentSelectedTokens.includes(MFA_TYPES[tokenName].ID)) {
        return true;
    }
    if (MFA_TYPES[tokenName] && MFA_TYPES[tokenName].ID === MFA_TYPES.RADIUS_OTP.ID) {
        if (
            !currentSelectedTokens.includes(MFA_TYPES.RADIUS_OTP.ID) &&
            (currentSelectedTokens.length || !radiusEnabled)
        ) {
            return false;
        }
    } else if (currentSelectedTokens.includes(MFA_TYPES.RADIUS_OTP.ID)) {
        return false;
    }

    if (currentSelectedTokens && currentSelectedTokens.includes(MFA_TYPES.CUSTOM_MFA.ID)) {
        return false;
    }
    if (currentSelectedTokens && currentSelectedTokens.includes(MFA_TYPES.CHROME.ID)) {
        if (
            (MFA_TYPES[tokenName] && MFA_TYPES[tokenName].ID === MFA_TYPES.HARDWARE.ID) ||
            MFA_TYPES[tokenName].ID === MFA_TYPES.OTP.ID
        ) {
            return false;
        }
    }
    if (
        currentSelectedTokens &&
        (currentSelectedTokens.includes(MFA_TYPES.DESKTOP_APP.ID) ||
            currentSelectedTokens.includes(MFA_TYPES.MS_TEAMS.ID))
    ) {
        if (
            MFA_TYPES[tokenName] &&
            (MFA_TYPES[tokenName].ID === MFA_TYPES.OTP.ID || MFA_TYPES[tokenName].ID === MFA_TYPES.HARDWARE.ID)
        ) {
            return false;
        }
    }
    if (
        currentSelectedTokens &&
        (currentSelectedTokens.includes(MFA_TYPES.HARDWARE.ID) || currentSelectedTokens.includes(MFA_TYPES.OTP.ID))
    ) {
        if (
            MFA_TYPES[tokenName] &&
            (MFA_TYPES[tokenName].ID === MFA_TYPES.DESKTOP_APP.ID ||
                MFA_TYPES[tokenName].ID === MFA_TYPES.CHROME.ID ||
                MFA_TYPES[tokenName].ID === MFA_TYPES.MS_TEAMS.ID)
        ) {
            return false;
        }
    }
    if (desktopEnabled) {
        if (modeType === POLICY_MODE_TYPES.OFFLINE.value && MFA_TYPES[tokenName].ID !== MFA_TYPES.HARDWARE.ID) {
            return false;
        }
        if (
            MFA_TYPES[tokenName] &&
            (MFA_TYPES[tokenName].ID === MFA_TYPES.CHROME.ID || MFA_TYPES[tokenName].ID === MFA_TYPES.DESKTOP_APP.ID)
        ) {
            return false;
        }
    } else if (MFA_TYPES[tokenName].ID === MFA_TYPES.PING_ADAPTER.ID) {
        return pingFedEnabled;
    }
    return configuredTokens.includes(MFA_TYPES[tokenName].ID);
};

export const getMatchingByOptions = (policyData, accordingToIpFFEnabled) => {
    const matchingByOptions = Object.values(POLICY_MATCHING_BY_OPTIONS)
        .filter((option) => {
            if (option.value === POLICY_MATCHING_BY_OPTIONS.IP.value) {
                if (!accordingToIpFFEnabled) {
                    return false;
                }
                if (
                    (policyData.kerberos_enabled && !policyData.ntlm_enabled && !policyData.ldap_enabled) ||
                    policyData.oidc_enabled
                ) {
                    return true;
                }
                return false;
            }
            if (option.value === POLICY_MATCHING_BY_OPTIONS.TGT.value) {
                if (policyData.kerberos_enabled && !policyData.ntlm_enabled) {
                    return true;
                }
                return false;
            } else if (option.value === POLICY_MATCHING_BY_OPTIONS.CLIENT.value) {
                if (
                    policyData.ldap_enabled ||
                    policyData.radius_enabled ||
                    policyData.ping_fed_enabled ||
                    policyData.relying_parties_enabled ||
                    policyData.desktop_enabled
                ) {
                    return false;
                }
            }
            return true;
        })
        .map((option) => {
            if (accordingToIpFFEnabled) {
                if (option.value === POLICY_MATCHING_BY_OPTIONS.CLIENT.value) {
                    return {
                        ...option,
                        label: POLICY_MATCHING_BY_OPTIONS_SOURCE_HOSTNAME_LABEL
                    };
                }
            }
            return option;
        });
    return matchingByOptions;
};

const shouldShowInCurrentMode = (token, isSaas) => {
    const tokenOptionsToHide = isSaas ? [MFA_TYPES.RADIUS_OTP.ID, MFA_TYPES.PING_ADAPTER.ID] : [];
    return !tokenOptionsToHide.includes(token.ID);
};

export const convertCountriesDataToSelectOptions = (countriesData) => {
    return countriesData.map(({ code, name }) => ({
        value: code,
        label: name
    }));
};
